import React, { useState } from "react";
import { BookOpen, ArrowUpRight } from "lucide-react";

export default function FreeResources() {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = [
    "All",
    "Influence",
    "AI for BA/PM",
    "Career Growth",
    "Visibility"
  ];

  const resources = [
    {
      title: "90 Day BA / PM Onboarding Checklist",
      category: "Influence",
      desc: "Hit the ground running in your new BA or PM role with an actionable, phase-by-phase checklist designed to build authority within 90 days.",
      link: "https://academy.suhasinibasa.com/web/checkout/6aa9664e137a6e79162142a3"
    },
    {
      title: "AI PM Pack",
      category: "AI for BA/PM",
      desc: "Transform your BA and PM workflow with a free, practical AI prompt pack designed for real project tasks and faster execution.",
      link: "https://academy.suhasinibasa.com/web/checkout/6a9f86475d1072413a13a9b9"
    },
    {
      title: "BA → PM Switch Kit",
      category: "Career Growth",
      desc: "Position your existing Business Analyst experience into Project Management language and fast-track your career transition with this free, practical toolkit.",
      link: "https://academy.suhasinibasa.com/web/checkout/6a9f80ad5ad83ba898d93037"
    },
    {
      title: "Linkedin Growth Kit",
      category: "Visibility",
      desc: "Optimize your LinkedIn profile to showcase your true BA and PM expertise, attract recruiters, and boost your career visibility.",
      link: "https://academy.suhasinibasa.com/web/checkout/6a9ed302517e5c7f25d4e0fb"
    }
  ];

  const filteredResources = selectedCategory === "All"
    ? resources
    : resources.filter(res => res.category === selectedCategory);

  return (
    <div className="relative bg-obsidian text-slate-100 min-h-screen">
      <div className="noise-overlay" />

      {/* Header */}
      <section className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-12 border-b border-white/5 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-left space-y-6 relative z-10">
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">RESOURCE SHELF</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            Practical resources to help you grow your BA/PM career
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-normal">
            Equip your career with free, on-demand templates, evaluation metrics, and strategic conversation guides. Zero empty theory.
          </p>
        </div>
      </section>

      {/* Filters & Grid */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-navy relative border-b border-white/5">
        <div className="max-w-7xl mx-auto space-y-12">

          {/* Categories Selector */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`category-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 font-display text-xs uppercase tracking-wider border transition-colors duration-200 ${
                  selectedCategory === cat 
                    ? "bg-gold text-obsidian border-gold font-bold" 
                    : "bg-obsidian/40 text-slate-400 border-white/5 hover:bg-obsidian/80 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((res, index) => (
              <div 
                key={index}
                className="bg-obsidian border border-white/5 p-6 sm:p-8 flex flex-col justify-between hover:border-gold/20 transition-all duration-300"
              >
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-[10px] text-gold uppercase tracking-widest font-bold">
                      {res.category}
                    </span>
                    <BookOpen className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white leading-snug">{res.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{res.desc}</p>
                </div>

                <a
                  href={res.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-button-${index}`}
                  className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gold hover:text-white transition-colors duration-200"
                >
                  <span>Get Free Resource</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

        </div>
      </section>

    </div>
  );
}
